import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, fmtMoney } from "@/lib/api";
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { inputCls } from "@/components/Modal";

const COLORS = ["#1a3c2e", "#d4891a", "#2c5f49", "#e8a941", "#3a6b8c", "#b33a3a"];

export default function Reports() {
  const [tab, setTab] = useState("sales");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");

  const params = { start: start ? start + "T00:00:00" : undefined, end: end ? end + "T23:59:59" : undefined };

  const { data: sales } = useQuery({ queryKey: ["report-sales", start, end], queryFn: () => api.get("/reports/sales", { params }).then((r) => r.data), enabled: tab === "sales" });
  const { data: pnl } = useQuery({ queryKey: ["report-pnl", start, end], queryFn: () => api.get("/reports/profit-loss", { params }).then((r) => r.data), enabled: tab === "pnl" });
  const { data: stock } = useQuery({ queryKey: ["report-stock"], queryFn: () => api.get("/reports/stock-valuation").then((r) => r.data), enabled: tab === "stock" });
  const { data: cash } = useQuery({ queryKey: ["report-cash", start], queryFn: () => api.get("/reports/cashbook", { params: { date: start || undefined } }).then((r) => r.data), enabled: tab === "cash" });

  const tabs = [
    { id: "sales", label: "Sales" },
    { id: "pnl", label: "Profit & Loss" },
    { id: "stock", label: "Stock Valuation" },
    { id: "cash", label: "Cash Book" },
  ];

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <div className="label-cap">Analytics</div>
        <h1 className="font-heading text-3xl mt-1">Reports</h1>
      </div>

      <div className="flex gap-2 border-b border-[rgba(28,28,30,0.06)]">
        {tabs.map((t) => (
          <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === t.id ? "border-agri-amber text-agri-forest" : "border-transparent text-agri-ink-2 hover:text-agri-ink"}`}>{t.label}</button>
        ))}
      </div>

      <div className="card-soft p-4 flex flex-wrap items-end gap-3">
        <div><label className="block text-xs text-agri-ink-2 mb-1">{tab === "cash" ? "Date" : "From"}</label><input type="date" value={start} onChange={(e) => setStart(e.target.value)} className={inputCls + " w-44"} /></div>
        {tab !== "cash" && tab !== "stock" && <div><label className="block text-xs text-agri-ink-2 mb-1">To</label><input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className={inputCls + " w-44"} /></div>}
      </div>

      {tab === "sales" && sales && (
        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <Stat label="Revenue" value={fmtMoney(sales.total_revenue)} />
            <Stat label="COGS" value={fmtMoney(sales.total_cogs)} />
            <Stat label="Gross Profit" value={fmtMoney(sales.gross_profit)} tone="amber" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <ChartCard title="By Category">
              <BarChart data={sales.by_category}><CartesianGrid stroke="rgba(28,28,30,0.06)" /><XAxis dataKey="name" fontSize={11} /><YAxis fontSize={11} tickFormatter={(v) => v >= 1000 ? `${v / 1000}k` : v} /><Tooltip /><Bar dataKey="revenue" fill="#1a3c2e" /></BarChart>
            </ChartCard>
            <ChartCard title="By Salesperson">
              <BarChart data={sales.by_salesperson}><CartesianGrid stroke="rgba(28,28,30,0.06)" /><XAxis dataKey="name" fontSize={11} /><YAxis fontSize={11} tickFormatter={(v) => v >= 1000 ? `${v / 1000}k` : v} /><Tooltip /><Bar dataKey="revenue" fill="#d4891a" /></BarChart>
            </ChartCard>
          </div>
          <Table title="Top products by revenue" rows={sales.by_product.slice(0, 20)} cols={[{ k: "name", l: "Product" }, { k: "revenue", l: "Revenue", money: true, align: "right" }]} />
        </div>
      )}

      {tab === "pnl" && pnl && (
        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-5">
            <Stat label="Revenue" value={fmtMoney(pnl.revenue)} />
            <Stat label="COGS" value={fmtMoney(pnl.cogs)} />
            <Stat label="Gross Profit" value={fmtMoney(pnl.gross_profit)} tone="forest" />
            <Stat label="Expenses" value={fmtMoney(pnl.expenses)} />
            <Stat label="Net Profit" value={fmtMoney(pnl.net_profit)} tone={pnl.net_profit >= 0 ? "amber" : "danger"} />
          </div>
          <ChartCard title="Expenses by category">
            <PieChart>
              <Pie data={pnl.by_expense_category} dataKey="value" nameKey="name" outerRadius={110} label>
                {pnl.by_expense_category.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip /><Legend />
            </PieChart>
          </ChartCard>
        </div>
      )}

      {tab === "stock" && stock && (
        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <Stat label="Inventory @ Cost" value={fmtMoney(stock.purchase_value)} />
            <Stat label="Inventory @ Selling" value={fmtMoney(stock.selling_value)} />
            <Stat label="Potential Profit" value={fmtMoney(stock.potential_profit)} tone="amber" />
          </div>
          <Table title="Inventory valuation" rows={stock.products}
            cols={[{ k: "name", l: "Product" }, { k: "category", l: "Category" }, { k: "stock", l: "Stock", align: "right" }, { k: "purchase_value", l: "Cost Value", money: true, align: "right" }, { k: "selling_value", l: "Sell Value", money: true, align: "right" }]} />
        </div>
      )}

      {tab === "cash" && cash && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            <Stat label="Cash Sales" value={fmtMoney(cash.cash_sales)} />
            <Stat label="Credit Collections" value={fmtMoney(cash.credit_collections)} />
            <Stat label="Supplier Payments" value={fmtMoney(cash.supplier_payments)} tone="danger" />
            <Stat label="Expenses" value={fmtMoney(cash.expenses)} tone="danger" />
          </div>
          <div className="card-soft p-6 bg-agri-forest text-white">
            <div className="label-cap text-white/60">Net flow ({cash.date})</div>
            <div className={`font-heading text-4xl mt-2 tabular-nums ${cash.net >= 0 ? "text-agri-amber" : "text-red-300"}`}>{fmtMoney(cash.net)}</div>
            <div className="text-xs text-white/70 mt-2 tabular-nums">Inflow {fmtMoney(cash.inflow)} · Outflow {fmtMoney(cash.outflow)}</div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone }) {
  const c = tone === "amber" ? "text-agri-amber-dark" : tone === "danger" ? "text-agri-danger" : "text-agri-forest";
  return <div className="card-soft p-5"><div className="label-cap">{label}</div><div className={`font-heading text-2xl mt-2 tabular-nums ${c}`}>{value}</div></div>;
}
function ChartCard({ title, children }) {
  return <div className="card-soft p-5"><h3 className="font-heading text-xl mb-3">{title}</h3><div className="h-72"><ResponsiveContainer>{children}</ResponsiveContainer></div></div>;
}
function Table({ title, rows, cols }) {
  return (
    <div className="card-soft overflow-hidden">
      <div className="p-4 font-heading text-xl">{title}</div>
      <div className="overflow-visible sm:overflow-auto sm:max-h-[60vh]">
        <table className="w-full text-sm rtable">
          <thead className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-agri-ink/10 text-[11px] uppercase tracking-wider text-agri-ink-2/70">
            <tr>{cols.map((c) => <th key={c.k} className={`px-4 py-3 font-semibold whitespace-nowrap ${c.align === "right" ? "text-right" : "text-left"}`}>{c.l}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-agri-ink/[0.06] hover:bg-agri-bg/40 transition-colors">
                {cols.map((c, ci) => <td key={c.k} data-label={c.l} className={`px-4 py-3 ${ci === 0 ? "rt-primary" : ""} ${c.align === "right" ? "text-right tabular-nums" : ""}`}>{c.money ? fmtMoney(r[c.k]) : r[c.k]}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
